// Payment System v3.0 PR 9 — Spread Note Financial Closing wizard (design
// spec §8.5). Route:
// /portfolio-admin/loans/:spreadNoteId/financial-closing. Four steps:
//   1. Pre-flight — Spread Note + existing assumed loan summary; computed
//      Path A/B/C with remaining-term breakdown; inline "Mature Bridge"
//      action when the bridge is still active so the operator doesn't have
//      to leave the page.
//   2. Subscriptions — investor inputs (mirrors InvestorClosingWizard's
//      tier/amount/investor-id editor).
//   3. Preview — closing disbursements + path-specific existing-loan
//      disposition (retire-at-par + §453B for Path A; preserved-under-
//      assumption for Path B/C).
//   4. Confirm — Execute button POSTs and redirects back to the Spread Note
//      detail page.

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
} from "@/design-system/components/ui";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { WaterfallBlockedNotice } from "@/components/payments/WaterfallBlockedNotice";
import { PreContributionScopeRedirect } from "@/components/portfolioAdmin/PreContributionScopeRedirect";
import { useLoan } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  useExecuteFinancialClosing,
  useMatureBridge,
  usePreviewFinancialClosing,
  useResaleLink,
} from "@/hooks/portfolioAdmin/useResale";
import type {
  FinancialClosingPreview,
  SubscriptionInput,
} from "@/types/resale";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

type Step = 1 | 2 | 3 | 4;

function formatCurrency(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

interface SubscriptionRow extends SubscriptionInput {
  rowId: string;
}

function makeRow(): SubscriptionRow {
  return {
    rowId: crypto.randomUUID(),
    investorId: "",
    tier: "tier_a",
    amount: 0,
  };
}

export function FinancialClosingWizard() {
  const { loanId: spreadNoteLoanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const spreadNoteQuery = useLoan(spreadNoteLoanId);
  const resaleLinkQuery = useResaleLink(spreadNoteLoanId);
  const matureBridge = useMatureBridge(spreadNoteLoanId ?? "");
  const preview = usePreviewFinancialClosing(spreadNoteLoanId ?? "");
  const execute = useExecuteFinancialClosing(spreadNoteLoanId ?? "");
  const tz = useUserTimeZone();

  const [step, setStep] = useState<Step>(1);
  const [closingDate, setClosingDate] = useState(() => isoDateInTz(new Date(), tz));
  const [poolId, setPoolId] = useState("");
  const [expectedPoolVersionId, setExpectedPoolVersionId] = useState("0");
  const [pathCElected, setPathCElected] = useState(false);
  const [rows, setRows] = useState<SubscriptionRow[]>([makeRow()]);
  const [previewData, setPreviewData] =
    useState<FinancialClosingPreview | null>(null);

  const spreadNote = spreadNoteQuery.data;
  const link = resaleLinkQuery.data;
  // Bridge status derived from the ResaleLink: if BridgeMaturedDate is set,
  // the bridge has been explicitly matured; otherwise still active.
  const bridgeActive = !!link && link.bridgeMaturedDate == null;

  const subscriptions = useMemo<SubscriptionInput[]>(
    () =>
      rows
        .filter((r) => r.investorId.trim().length > 0 && r.amount > 0)
        .map(({ investorId, tier, amount }) => ({ investorId, tier, amount })),
    [rows],
  );

  const subscriptionsValid =
    subscriptions.length > 0 &&
    rows.every(
      (r) => r.investorId.trim().length > 0 && r.amount > 0,
    );
  const previewInputsValid =
    !!closingDate && !!poolId && subscriptionsValid && !bridgeActive;

  async function handleMatureBridge() {
    if (!spreadNoteLoanId) return;
    try {
      await matureBridge.mutateAsync();
      toast.success("Bridge period matured — Financial Closing is now available.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handlePreview() {
    if (!spreadNoteLoanId || !previewInputsValid) return;
    try {
      const data = await preview.mutateAsync({
        spreadNotePoolId: poolId,
        closingDate,
        expectedPoolVersionId: Number(expectedPoolVersionId),
        subscriptions,
        pathCElected,
      });
      setPreviewData(data);
      setStep(3);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleExecute() {
    if (!spreadNoteLoanId || !previewData) return;
    try {
      const result = await execute.mutateAsync({
        spreadNotePoolId: poolId,
        closingDate,
        expectedPoolVersionId: Number(expectedPoolVersionId),
        subscriptions,
        pathCElected,
      });
      const pathSummary = result.path === "A"
        ? `Path A — existing loan retired at par; §453B fired on ${result.section453BSegments.length} uncontributed segment(s).`
        : `Path ${result.path} — existing loan preserved under assumption.`;
      toast.success(
        `Financial Closing committed (${result.subscriptionCount} subscription(s), ${result.ssnIssuedCount} SSN(s) issued). ${pathSummary}`,
      );
      navigate(`/portfolio-admin/loans/${spreadNoteLoanId}`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="text"
            size="sm"
            onClick={() => navigate(`/portfolio-admin/loans/${spreadNoteLoanId}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Spread Note
          </Button>
          <h1 className="mt-2 text-2xl font-semibold">
            Spread Note Financial Closing
          </h1>
          <p className="text-sm text-muted-foreground">
            Runs the Spread Note pool's first investor closing and applies
            the Tiered Resolution to the existing assumed loan.
          </p>
        </div>
        <StepIndicator step={step} />
      </div>

      {/* ----- Step 1 — Pre-flight ---------------------------------- */}
      {step === 1 && (
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">1. Pre-flight</h2>

          {spreadNote && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-4 text-sm">
              <h3 className="font-semibold">Spread Note</h3>
              <div className="grid grid-cols-2 gap-2">
                <Figure
                  label="Loan number"
                  stringValue={spreadNote.loanNumber}
                />
                <Figure
                  label="Current balance"
                  value={spreadNote.currentBalance}
                />
                <Figure
                  label="Bridge active?"
                  stringValue={bridgeActive ? "Yes" : "No"}
                />
                <Figure
                  label="Bridge matured"
                  stringValue={link?.bridgeMaturedDate ?? "—"}
                />
              </div>
            </div>
          )}

          {bridgeActive && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <div>
                The 12-month Resale Bridge Period is still active. Mature it
                explicitly to enable the Financial Closing.
              </div>
              <Button
                onClick={handleMatureBridge}
                disabled={matureBridge.isPending}
              >
                {matureBridge.isPending ? "Maturing..." : "Mature bridge now"}
              </Button>
            </div>
          )}

          {link && (
            <div className="space-y-2 rounded-md border bg-muted/30 p-4 text-sm">
              <h3 className="font-semibold">Resale linkage</h3>
              <div className="grid grid-cols-2 gap-2">
                <Figure
                  label="Bridge started"
                  stringValue={link.bridgeStartedDate}
                />
                <Figure
                  label="Bridge matured"
                  stringValue={link.bridgeMaturedDate ?? "—"}
                />
                <Figure
                  label="Resale Closing payment"
                  stringValue={link.resaleClosingPaymentId.slice(0, 8)}
                />
                <Figure
                  label="Original loan id"
                  stringValue={link.originalLoanId.slice(0, 8)}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Closing date">
              <Input
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
              />
            </FormField>
            <FormField label="Spread Note pool id">
              <Input
                value={poolId}
                onChange={(e) => setPoolId(e.target.value)}
                placeholder="UUID"
              />
            </FormField>
            <FormField label="Expected pool version id (optimistic concurrency)">
              <Input
                type="number"
                value={expectedPoolVersionId}
                onChange={(e) => setExpectedPoolVersionId(e.target.value)}
              />
            </FormField>
            <FormField label="Path C elected? (operator-elected variant of B)">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={pathCElected}
                  onChange={(e) => setPathCElected(e.target.checked)}
                />
                <span className="text-muted-foreground">
                  Only relevant when remaining term ≥ 18 months
                </span>
              </label>
            </FormField>
          </div>

          <PathExplanation />

          <div className="flex justify-end">
            <Button
              onClick={() => setStep(2)}
              disabled={!poolId || !closingDate || bridgeActive}
            >
              Continue to subscriptions →
            </Button>
          </div>
        </Card>
      )}

      {/* ----- Step 2 — Subscriptions ------------------------------- */}
      {step === 2 && (
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">2. Subscriptions</h2>
          <p className="text-sm text-muted-foreground">
            One row per investor commitment. Tier must be tier_a, tier_b, or
            tier_c1; amount in USD.
          </p>

          <Table>
            <THead>
              <TR>
                <TH>Investor id (UUID)</TH>
                <TH>Tier</TH>
                <TH className="text-right">Amount</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {rows.map((r) => (
                <TR key={r.rowId}>
                  <TD>
                    <Input
                      value={r.investorId}
                      onChange={(e) =>
                        setRows((rs) =>
                          rs.map((x) =>
                            x.rowId === r.rowId
                              ? { ...x, investorId: e.target.value }
                              : x,
                          ),
                        )
                      }
                      placeholder="00000000-0000-0000-0000-000000000000"
                    />
                  </TD>
                  <TD>
                    <select
                      value={r.tier}
                      onChange={(e) =>
                        setRows((rs) =>
                          rs.map((x) =>
                            x.rowId === r.rowId
                              ? {
                                  ...x,
                                  tier: e.target.value as SubscriptionInput["tier"],
                                }
                              : x,
                          ),
                        )
                      }
                      className="rounded border bg-background p-1 text-sm"
                    >
                      <option value="tier_a">tier_a</option>
                      <option value="tier_b">tier_b</option>
                      <option value="tier_c1">tier_c1</option>
                    </select>
                  </TD>
                  <TD className="text-right">
                    <Input
                      type="number"
                      value={String(r.amount || "")}
                      onChange={(e) =>
                        setRows((rs) =>
                          rs.map((x) =>
                            x.rowId === r.rowId
                              ? { ...x, amount: Number(e.target.value) }
                              : x,
                          ),
                        )
                      }
                      placeholder="100000"
                      className="text-right"
                    />
                  </TD>
                  <TD>
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() =>
                        setRows((rs) =>
                          rs.length > 1
                            ? rs.filter((x) => x.rowId !== r.rowId)
                            : rs,
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          <div className="flex items-center justify-between text-sm">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setRows((rs) => [...rs, makeRow()])}
            >
              <Plus className="mr-2 h-4 w-4" /> Add subscription
            </Button>
            <div className="font-medium">
              Gross:{" "}
              {formatCurrency(subscriptions.reduce((a, s) => a + s.amount, 0))}
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(1)}>
              ← Back
            </Button>
            <Button
              onClick={handlePreview}
              disabled={!previewInputsValid || preview.isPending}
            >
              {preview.isPending ? "Computing..." : "Compute preview →"}
            </Button>
          </div>
        </Card>
      )}

      {/* ----- Step 3 — Preview ------------------------------------- */}
      {step === 3 && previewData && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">3. Preview</h2>
            <Badge status={previewData.projectedPath === "A" ? "gold" : "info"}>
              Path {previewData.projectedPath}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-md border p-4 text-sm">
            <Figure
              label="Existing loan"
              stringValue={previewData.existingLoanNumber}
            />
            <Figure
              label="Existing loan state"
              stringValue={previewData.existingLoanState}
            />
            <Figure
              label="Existing loan balance"
              value={previewData.existingLoanBalance}
            />
            <Figure
              label="Existing loan maturity"
              stringValue={previewData.existingLoanMaturityDate}
            />
            <Figure
              label="Months remaining"
              stringValue={String(previewData.monthsRemaining)}
            />
            <Figure
              label="Closing gross proceeds"
              value={previewData.closingPreview.grossProceeds}
              emphasis
            />
          </div>

          <div
            className={
              previewData.projectedPath === "A"
                ? "rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                : "rounded-md border border-blue-300 bg-blue-50 p-3 text-xs text-blue-900"
            }
          >
            {previewData.pathDescription}
          </div>

          {previewData.projectedPath === "A" &&
            previewData.section453BSegments.length > 0 && (
              <div className="rounded-md border bg-muted/30 p-3 text-xs">
                <strong>§453B Per-Segment Tax Notices</strong> will be issued
                for segments:{" "}
                <span className="font-mono">
                  {previewData.section453BSegments.join(", ")}
                </span>
                .
              </div>
            )}

          {previewData.closingPreview.wf2BlockedReason && (
            <WaterfallBlockedNotice
              reason={previewData.closingPreview.wf2BlockedReason}
              subject="financial closing"
              scopeNote="The Spread Note pool's waterfall split is what is blocked; the path and loan details above are unaffected."
            />
          )}

          {previewData.closingPreview.wf2Disbursements.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold">
                WF2 disbursements (closing-event slice)
              </h3>
              <Table>
                <THead>
                  <TR>
                    <TH>#</TH>
                    <TH>Obligation</TH>
                    <TH>Recipient</TH>
                    <TH className="text-right">Amount</TH>
                  </TR>
                </THead>
                <TBody>
                  {previewData.closingPreview.wf2Disbursements.map((d, i) => (
                    <TR key={`wf2-${i}`}>
                      <TD>{d.priorityPosition}</TD>
                      <TD>{d.obligationTypeCode}</TD>
                      <TD>
                        {d.recipientDisplayName ?? d.recipientResolution}
                      </TD>
                      <TD className="text-right">{formatCurrency(d.amount)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(2)}>
              ← Back
            </Button>
            <Button
              onClick={() => setStep(4)}
              disabled={Boolean(previewData.closingPreview.wf2BlockedReason)}
            >
              Continue to confirm →
            </Button>
          </div>
        </Card>
      )}

      {/* ----- Step 4 — Confirm ------------------------------------- */}
      {step === 4 && previewData && (
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">4. Confirm Financial Closing</h2>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            Atomic operation. The Spread Note pool's first investor closing
            will run (per PR 7's ClosingEngine), and the existing assumed
            loan will be resolved via{" "}
            <strong>Path {previewData.projectedPath}</strong>.
            {previewData.projectedPath === "A"
              ? " The existing loan will be retired at par and §453B Per-Segment Tax Notices will fire for its uncontributed segments."
              : " The existing loan stays under assumption — no §453B disposition."}
          </div>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(3)}>
              ← Back to preview
            </Button>
            <Button onClick={handleExecute} disabled={execute.isPending}>
              {execute.isPending ? (
                "Executing..."
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" /> Execute Financial Closing
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function PathExplanation() {
  return (
    <div className="space-y-1 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
      <div>
        <strong className="text-foreground">Path A</strong> (remaining term
        &lt; 18 months): existing loan retires at par; §453B disposition fires
        for its uncontributed segments.
      </div>
      <div>
        <strong className="text-foreground">Path B</strong> (remaining term ≥
        18 months): existing loan preserved under assumption; no §453B.
      </div>
      <div>
        <strong className="text-foreground">Path C</strong>: operator-elected
        variant of B (same mechanics, different audit tag).
      </div>
    </div>
  );
}

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {[1, 2, 3, 4].map((n) => (
        <div
          key={n}
          className={
            n === step
              ? "rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground"
              : "rounded-full bg-muted px-3 py-1"
          }
        >
          Step {n}
        </div>
      ))}
    </div>
  );
}

function Figure({
  label,
  value,
  stringValue,
  emphasis,
}: {
  label: string;
  value?: number;
  stringValue?: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={emphasis ? "text-base font-semibold" : "text-sm font-medium"}
      >
        {stringValue ?? (value !== undefined ? formatCurrency(value) : "—")}
      </div>
    </div>
  );
}

// Route entry — pre-contribution scope redirects deep links to the loan
// detail page while the stage-2 wizards are hidden.
export default function FinancialClosingWizardRoute() {
  return (
    <PreContributionScopeRedirect>
      <FinancialClosingWizard />
    </PreContributionScopeRedirect>
  );
}
